import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCustomDialog } from '../../context/DialogContext';

const StaffManagement = ({ token }) => {
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null); // null when adding, {id, name, username, role} when editing
    const [formData, setFormData] = useState({ staff_name: '', username: '', password: '', role: 'chef', mobile_number: '', address: '' });
    const [error, setError] = useState('');

    const fetchStaff = useCallback(async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_staff_mgmt.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success) setStaff(json.staff);
        } catch (err) {
            console.error("Staff fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    const handleOpenAdd = () => {
        setError('');
        setEditingStaff(null);
        setFormData({ staff_name: '', username: '', password: '', role: 'chef', mobile_number: '', address: '' });
        setShowModal(true);
    };

    const handleOpenEdit = (s) => {
        setError('');
        setEditingStaff(s);
        setFormData({ staff_name: s.staff_name, username: s.username, password: '', role: s.role, mobile_number: s.mobile_number || '', address: s.address || '' });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const action = editingStaff ? 'update' : 'create';
        const payload = editingStaff ? { ...formData, id: editingStaff.id } : formData;

        try {
            const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_staff_mgmt.php?action=${action}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const json = await res.json();
            if (json.success) {
                setShowModal(false);
                setFormData({ staff_name: '', username: '', password: '', role: 'chef', mobile_number: '', address: '' });
                setEditingStaff(null);
                fetchStaff();
            } else {
                setError(json.message);
            }
        } catch (err) {
            setError("Server error.");
        }
    };

    const handleDelete = async (id) => {
        if (!await customConfirm("Remove this staff member?", "Remove Staff", "danger")) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_staff_mgmt.php?action=delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id })
            });
            const json = await res.json();
            if (json.success) fetchStaff();
        } catch (err) {
            await customAlert("Error removing staff.", "Error", "danger");
        }
    };

    const handleCopyDetails = (s) => {
        const text = `Hello ${s.staff_name},\n\nHere are your login details for the Kitchen/Staff Panel:\n\n*Login URL:* https://menu.searchisend.in/staff-login\n*Username:* ${s.username}\n*Password:* (The password assigned by your admin)\n\nPlease log in to view and manage orders.`;
        navigator.clipboard.writeText(text);
        customAlert("Login details copied to clipboard! You can now paste and send this message to your staff.", "Copied successfully", "success");
    };

    return (
        <div style={{ padding: '20px' }}>
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> Back to Dashboard
            </Link>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h1 style={{ fontSize: '1.8rem', fontWeight: '800', margin: 0 }}>Staff Management</h1>
                    <p style={{ color: '#64748b', margin: '5px 0 0 0' }}>Create and manage accounts for your Chefs and Waiters.</p>
                </div>
                <button
                    onClick={handleOpenAdd}
                    style={{ background: 'var(--primary-orange)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '50px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(234,88,12,0.3)' }}
                >
                    <i className="fas fa-plus" style={{ marginRight: '8px' }} /> Add Staff Member
                </button>
            </div>

            {/* Kitchen Login Link */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '16px 20px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '28px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <div>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05em', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-link" style={{ color: 'var(--primary-orange)' }}></i> Kitchen / Staff Login URL
                    </h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.85em', marginBottom: '4px' }}>Share this exact link with your staff so they can log in to view orders.</p>
                    <a href="https://menu.searchisend.in/staff-login" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-orange)', fontWeight: '600', textDecoration: 'none', fontSize: '0.95em', wordBreak: 'break-all' }}>
                        https://menu.searchisend.in/staff-login
                    </a>
                </div>
                <button
                    onClick={() => {
                        navigator.clipboard.writeText(`https://menu.searchisend.in/staff-login`);
                        customAlert('Staff login link copied to clipboard!', 'Copied', 'success');
                    }}
                    style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569', padding: '8px 16px', borderRadius: '50px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85em', transition: 'all 0.2s' }}
                >
                    <i className="far fa-copy"></i> Copy Link
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading staff...</div>
            ) : staff.length > 0 ? (
                <div className="table-responsive" style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <tr>
                                <th style={{ padding: '15px 20px', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>NAME</th>
                                <th className="hide-on-mobile" style={{ padding: '15px 20px', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>USERNAME</th>
                                <th className="hide-on-mobile" style={{ padding: '15px 20px', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>PHONE</th>
                                <th className="hide-on-mobile" style={{ padding: '15px 20px', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>ROLE</th>
                                <th style={{ padding: '15px 20px', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staff.map(s => (
                                <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }} className="table-row-hover">
                                    <td style={{ padding: '15px 20px', fontWeight: '600' }}>{s.staff_name}</td>
                                    <td className="hide-on-mobile" style={{ padding: '15px 20px', color: '#64748b' }}>{s.username}</td>
                                    <td className="hide-on-mobile" style={{ padding: '15px 20px', color: '#64748b' }}>{s.mobile_number || '-'}</td>
                                    <td className="hide-on-mobile" style={{ padding: '15px 20px' }}>
                                        <span style={{
                                            padding: '4px 12px',
                                            borderRadius: '50px',
                                            background: s.role === 'chef' ? '#fef3c7' : s.role === 'delivery' ? '#e0e7ff' : '#dcfce7',
                                            color: s.role === 'chef' ? '#b45309' : s.role === 'delivery' ? '#4338ca' : '#166534',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            textTransform: 'uppercase'
                                        }}>
                                            {s.role}
                                        </span>
                                    </td>
                                    <td style={{ padding: '15px 20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                                        <button onClick={() => handleCopyDetails(s)} title="Copy Login Details" style={{ color: '#3b82f6', border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <i className="far fa-copy" />
                                        </button>
                                        <button onClick={() => handleOpenEdit(s)} title="Edit Staff" style={{ color: 'var(--primary-orange)', border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>
                                            <i className="fas fa-edit" />
                                        </button>
                                        <button onClick={() => handleDelete(s.id)} title="Delete Staff" style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>
                                            <i className="fas fa-trash-alt" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '100px', background: '#f8fafc', borderRadius: '20px', border: '2px dashed #e2e8f0' }}>
                    <i className="fas fa-users" style={{ fontSize: '3rem', color: '#e2e8f0', marginBottom: '15px', display: 'block' }} />
                    <h3 style={{ margin: 0, color: '#64748b' }}>No staff members yet.</h3>
                    <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Add your first chef to give them access to the kitchen panel.</p>
                </div>
            )}

            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', maxWidth: '450px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <h2 style={{ margin: '0 0 20px 0' }}>{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>Full Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Chef Sanjay"
                                    value={formData.staff_name}
                                    onChange={e => setFormData({ ...formData, staff_name: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
                                    required
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>Username</label>
                                <input
                                    type="text"
                                    placeholder="e.g. chef_sanjay"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
                                    required
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>Password {editingStaff && <span style={{ fontWeight: 'normal', color: '#94a3b8' }}>(Optional)</span>}</label>
                                <input
                                    type="password"
                                    placeholder={editingStaff ? "Leave blank to keep current" : "Set a password"}
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
                                    required={!editingStaff}
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>Mobile Number <span style={{ fontWeight: 'normal', color: '#94a3b8' }}>(Optional)</span></label>
                                <input
                                    type="tel"
                                    placeholder="e.g. 9876543210"
                                    value={formData.mobile_number}
                                    onChange={e => setFormData({ ...formData, mobile_number: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>Home Address <span style={{ fontWeight: 'normal', color: '#94a3b8' }}>(Optional)</span></label>
                                <textarea
                                    placeholder="Enter full address for your records"
                                    value={formData.address}
                                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box', minHeight: '80px', fontFamily: 'inherit' }}
                                />
                            </div>
                            <div style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', fontWeight: '600' }}>Role</label>
                                <select
                                    value={formData.role}
                                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
                                >
                                    <option value="chef">Chef</option>
                                    <option value="waiter">Waiter</option>
                                    <option value="delivery">Delivery Partner</option>
                                </select>
                            </div>

                            {error && <p style={{ color: '#ef4444', fontSize: '0.9rem', marginBottom: '15px' }}>{error}</p>}

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '50px', border: '1px solid #e2e8f0', background: 'none', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                                <button type="submit" style={{ flex: 1, padding: '12px', borderRadius: '50px', border: 'none', background: 'var(--primary-orange)', color: '#fff', cursor: 'pointer', fontWeight: '700' }}>Save Staff</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffManagement;
